import {useEffect, useRef} from "react";
import {useMessages} from "i18n";
import BaseButton from "../components/BaseButton";
import {VisuallyHiddenLiveRegion} from "../components/Accessibility.styles";
import {AuthEyebrow, Sub} from "./LoginModal.styles";
import {Action, Card, FinePrint, Refusal, Stage, StopAction, Status, Title} from "./SignupLinkCard.styles";
import {outcomeReached, stopAvailable, stopSurface, type SignupLinkState} from "./signupLinkState";

/**
 * The unfinished-signup reminder's landing card (ui#1688): what `/login` shows in place of the login
 * modal when the reminder's link put a token in the URL fragment.
 *
 * Markup and copy only — every rule is in {@link ./signupLinkState}, and the presses are the caller's.
 * The one thing this component is responsible for is that the reader's ONLY way to spend the link is
 * to press the button on it: nothing here runs on mount, because mail scanners open this page.
 */
export interface SignupLinkCardProps {
	state: SignupLinkState;
	onConfirm: () => void;
	onStop: () => void;
}

const TITLE_ID = "signup-link-card-title";

export function SignupLinkCard({state, onConfirm, onStop}: SignupLinkCardProps) {
	const {login, signupLinkTitle, signupLinkStopTitle, signupLinkFinePrint} = useMessages();
	const copy = login.signupLink;
	const cardRef = useRef<HTMLElement>(null);

	/**
	 * What an assistive reader is told while a press is IN FLIGHT, when the only thing that changed is
	 * a disabled button's label — which screen readers do not reliably announce.
	 *
	 * Deliberately empty for every other state. The outcome states put their sentence ON SCREEN, and
	 * those elements are the live region themselves (see {@link Status} / {@link Refusal} below), so
	 * repeating the text here would put the same sentence in the accessibility tree twice.
	 */
	const busyAnnouncement = state.kind === "finishing"
		? login.code.finishing
		: (state.kind === "stopping" ? copy.stopping : "");

	// The pressed button is UNMOUNTED when its outcome arrives, which drops keyboard focus to the body
	// and silently loses the reader's place. Moving focus to the card puts them on the element whose
	// text just replaced the control they were standing on. Announcement and focus are separate jobs:
	// the live region above tells a screen reader what happened, this decides where the caret is.
	const settled = outcomeReached(state);
	useEffect(() => {
		if (settled) {
			cardRef.current?.focus();
		}
	}, [settled]);

	const liveRegion = (
		<VisuallyHiddenLiveRegion role="status" aria-live="polite" aria-atomic="true">
			{busyAnnouncement}
		</VisuallyHiddenLiveRegion>
	);
	/** The outcome sentences announce themselves, in place, with no hidden duplicate. */
	const announces = {role: "status", "aria-live": "polite", "aria-atomic": true} as const;

	if (stopSurface(state)) {
		return (
			<Stage>
				<Card ref={cardRef} tabIndex={-1} aria-labelledby={TITLE_ID}>
					{liveRegion}
					<AuthEyebrow>{copy.eyebrow}</AuthEyebrow>
					<Title as="h1" id={TITLE_ID}>{signupLinkStopTitle()}</Title>
					{state.kind === "stopped" && <Status {...announces}>{login.code.remindersStopped}</Status>}
					{state.kind === "nothingToStop" && (
						<Status {...announces}>{login.code.remindersNothingToStop}</Status>
					)}
					{(state.kind === "stopPrompt" || state.kind === "stopping") && (
						<>
							<Sub>{copy.stopLede}</Sub>
							<Action>
								<BaseButton
									variant="gold"
									fullWidth
									onClick={onStop}
									disabled={state.kind === "stopping"}
								>
									{state.kind === "stopping" ? copy.stopping : copy.stopConfirm}
								</BaseButton>
							</Action>
						</>
					)}
					{state.kind === "refused" && (
						// The api's own sentence when it answered, and ours only when it did not: a stop link
						// it refuses is one no retry can rescue, so this is news rather than a failed action.
						<Refusal {...announces}>{state.message ?? copy.stopFailed}</Refusal>
					)}
				</Card>
			</Stage>
		);
	}

	const finishing = state.kind === "finishing";
	const signingIn = state.kind === "signingIn";
	return (
		<Stage>
			<Card ref={cardRef} tabIndex={-1} aria-labelledby={TITLE_ID}>
				{liveRegion}
				<AuthEyebrow>{copy.eyebrow}</AuthEyebrow>
				<Title as="h1" id={TITLE_ID}>{signupLinkTitle()}</Title>
				{signingIn
					? (
						<Status {...announces}>
							{state.email === null ? copy.signingInAnonymous : copy.signingIn(state.email)}
						</Status>
					)
					: (
						<>
							<Sub>{copy.lede}</Sub>
							<Action>
								<BaseButton variant="gold" fullWidth onClick={onConfirm} disabled={finishing}>
									{finishing ? login.code.finishing : copy.confirm}
								</BaseButton>
							</Action>
						</>
					)}
				{/* Only before the press. Once the link is being spent, "changed your mind?" is no longer a
				    question this card can answer, and a control that cannot act is worse than none. */}
				{state.kind === "confirm" && (
					<FinePrint>
						{stopAvailable(state)
							? signupLinkFinePrint(
								<StopAction type="button" onClick={onStop}>{copy.stopAction}</StopAction>)
							: copy.stopElsewhere}
					</FinePrint>
				)}
			</Card>
		</Stage>
	);
}
